import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = { "On-peak": "#C2410C", "Mid-peak": "#B45309", "Off-peak": "#156F5B", Standard: "#0E7490" };
const FALLBACK = ["#156F5B", "#C2410C", "#B45309", "#0E7490", "#7C3AED", "#9CA3AF"];
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 12 };

const num = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

export const HOUR_LABEL = (h) => `${String(h).padStart(2, "0")}:00`;

/** Splits the period's electricity cost across the tariff bands that actually applied. */
export function TouBreakdown({ tou, currency }) {
  const data = useMemo(
    () => (tou?.periods || []).map((p, i) => ({ ...p, fill: COLORS[p.name] || FALLBACK[i % FALLBACK.length] })),
    [tou]
  );

  if (data.length === 0) {
    return <div className="h-56 flex items-center justify-center text-sm text-[#9CA3AF]">No priced hours yet</div>;
  }

  return (
    <div className="grid md:grid-cols-2 gap-6 items-center" data-testid="tou-breakdown">
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <PieChart>
            <Pie data={data} dataKey="cost" nameKey="name" innerRadius="55%" outerRadius="82%" paddingAngle={2} stroke="none">
              {data.map((p) => <Cell key={p.name} fill={p.fill} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `${num(v, 2)} ${currency}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="divide-y divide-[#F0F2F1]">
        {data.map((p) => (
          <div key={p.name} className="py-2.5 flex items-center justify-between gap-3" data-testid={`tou-row-${p.name}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: p.fill }} />
              <span className="text-sm font-heading font-semibold text-[#212121] truncate">{p.name}</span>
              <span className="k-sublabel shrink-0">@ {p.rate}</span>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm text-[#212121]">{num(p.cost, 2)} {currency}</div>
              <div className="k-sublabel">{num(p.kwh)} kWh · {p.share_pct ?? "—"}%</div>
            </div>
          </div>
        ))}
        {tou.effective_rate != null && (
          <p className="pt-3 k-sublabel" data-testid="tou-effective-rate">
            Blended rate {tou.effective_rate} {currency}/kWh across {num(tou.kwh)} kWh
          </p>
        )}
      </div>
    </div>
  );
}
